import { Tab } from "@headlessui/react";
import { Tooltip, TooltipProvider, TooltipTrigger } from "@radix-ui/react-tooltip";
import { useState } from "react";

import { AddReleaseLabelDialog } from "../AddReleaseLabelButton";
import {
  PendingDeletesByLabelId,
  PendingReleaseLabelMovesRegistry,
} from "../hooks/usePendingReleaseLabelMoves";

import CommitMessage from "./CommitMessage";
import DroppableVersionLabels from "./DroppableVersionLabels";
import EvaluationStatus from "./EvaluationStatus";
import useVersionDetails from "./hooks/useVersionDetails";
import { Label } from "./types";
import VersionHeader from "./VersionHeader";
import VersionMetadata from "./VersionMetadata";
import VersionTooltipContent from "./VersionTooltipContent";

import type {
  VersionSelectorVersion,
  VersionSelectorVersionId,
} from "@/components/VersionSelector/types";
import { LinkWithSearch } from "@/components/LinkWithSearch";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries/folder-entities";
import { ReportScore } from "@/types/evaluate";
import { classNames } from "@/utils/strings";

interface VersionTabProps {
  version: VersionSelectorVersion;
  isActiveVersion: boolean;
  isLatestVersion: boolean;
  latestVersionNumber?: number;
  versionNumber: string | undefined;
  counts: Record<number, number>;
  navigateToVersionLink: (versionNumber: number) => string;
  onAddReleaseLabel: (label: string, versionId: VersionSelectorVersionId) => Promise<unknown>;
  handleDynamicReleaseLabelClick: (release_label_group_id: number) => void;
  dynamicReleaseLabelsRegistry: Record<number, Label[]>;
  versionEvaluationScoresRegistry: Record<number, ReportScore | null>;
  handleABTestButtonClick: (promptId: number, promptVersionId: number) => void;
  handleBacktestButtonClick: (promptVersionId: number) => void;
  handleArchiveButtonClick: (promptVersionId: VersionSelectorVersionId) => void;
  handleUnarchiveButtonClick: (promptVersionId: VersionSelectorVersionId) => void;
  archivingVersionId: VersionSelectorVersionId | null;
  isArchiving: boolean;
  promptId: string | undefined;
  isWorkflow: boolean;
  entityType?: EntityType;
  onConfigureLabel?: (label: Label) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allLabels: any[];
  pendingReleaseLabelMoves?: PendingReleaseLabelMovesRegistry;
  pendingDeletesByLabelId?: PendingDeletesByLabelId;
  onPendingLabelClick?: (changelogId: number) => void;
}

const VersionTab = ({
  version,
  isActiveVersion,
  isLatestVersion,
  latestVersionNumber,
  versionNumber,
  counts,
  navigateToVersionLink,
  onAddReleaseLabel,
  handleDynamicReleaseLabelClick,
  dynamicReleaseLabelsRegistry,
  versionEvaluationScoresRegistry,
  handleABTestButtonClick,
  handleBacktestButtonClick,
  handleArchiveButtonClick,
  handleUnarchiveButtonClick,
  archivingVersionId,
  isArchiving,
  promptId,
  isWorkflow,
  entityType,
  onConfigureLabel,
  allLabels,
  pendingReleaseLabelMoves,
  pendingDeletesByLabelId,
  onPendingLabelClick,
}: VersionTabProps) => {
  const versionDetails = useVersionDetails(version);
  const dynamicReleaseLabels =
    typeof version.id === "number" ? dynamicReleaseLabelsRegistry[version.id] || [] : [];
  const [isAddLabelDialogOpen, setIsAddLabelDialogOpen] = useState(false);
  const { isDeployDisabled } = useRbac();
  const cannotDeployReleaseLabels = isDeployDisabled(
    isWorkflow ? EntityType.WORKFLOW : EntityType.PROMPT,
  );

  return (
    <>
      <Tab
        key={versionDetails.id}
        data-version-number={versionDetails.number}
        className={classNames(
          "group w-full select-none overflow-x-hidden outline-none",
          isActiveVersion ? "bg-blue-100" : "",
        )}
      >
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <LinkWithSearch
                path={navigateToVersionLink(versionDetails.number)}
                className="relative block"
              >
                <div className="relative mx-6 border-l border-gray-300 p-2 text-start">
                  <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-gray-500 bg-white" />

                  <VersionHeader
                    number={versionDetails.number}
                    counts={counts}
                    parentVersionNumber={versionDetails.parentVersionNumber}
                  />
                  <VersionMetadata
                    created_at={versionDetails.created_at}
                    author={versionDetails.author}
                  />

                  {versionDetails.commit_message && (
                    <CommitMessage message={versionDetails.commit_message} />
                  )}

                  <EvaluationStatus
                    id={versionDetails.id}
                    hasReportId={!!versionDetails.hasReportId}
                    versionEvaluationScoresRegistry={versionEvaluationScoresRegistry}
                  />

                  <DroppableVersionLabels
                    hasProtectedLabels={allLabels.some(label => label.is_protected)}
                    versionId={versionDetails.id}
                    versionNumber={versionDetails.number}
                    labels={versionDetails.labels}
                    dynamicReleaseLabels={dynamicReleaseLabels}
                    handleDynamicReleaseLabelClick={handleDynamicReleaseLabelClick}
                    isActiveVersion={isActiveVersion}
                    onOpenAddLabelDialog={() => setIsAddLabelDialogOpen(true)}
                    onConfigureLabel={onConfigureLabel}
                    pendingReleaseLabelMoves={pendingReleaseLabelMoves}
                    pendingDeletesByLabelId={pendingDeletesByLabelId}
                    onPendingLabelClick={onPendingLabelClick}
                    cannotDeployReleaseLabels={cannotDeployReleaseLabels}
                  />
                </div>
              </LinkWithSearch>
            </TooltipTrigger>

            <VersionTooltipContent
              entityType={entityType ?? (isWorkflow ? EntityType.WORKFLOW : EntityType.PROMPT)}
              id={versionDetails.id}
              dynamicReleaseLabelsRegistry={dynamicReleaseLabelsRegistry}
              allLabels={allLabels}
              promptId={promptId}
              handleABTestButtonClick={handleABTestButtonClick}
              handleBacktestButtonClick={handleBacktestButtonClick}
              handleArchiveButtonClick={handleArchiveButtonClick}
              handleUnarchiveButtonClick={handleUnarchiveButtonClick}
              archivingVersionId={archivingVersionId}
              isArchiving={isArchiving}
              reportId={versionDetails.reportId}
              isArchived={versionDetails.archived}
              versionNumber={versionNumber}
              number={versionDetails.number}
              isLatestVersion={isLatestVersion}
              latestVersionNumber={latestVersionNumber}
            />
          </Tooltip>
        </TooltipProvider>
      </Tab>
      <AddReleaseLabelDialog
        isOpen={isAddLabelDialogOpen}
        onOpenChange={setIsAddLabelDialogOpen}
        onSave={label => onAddReleaseLabel(label, versionDetails.id)}
        isWorkflow={isWorkflow}
        entityType={entityType}
      />
    </>
  );
};

export default VersionTab;
